import { MultiSelect, Select, Text, TextInput } from "@mantine/core";
import { useClickOutside } from "@mantine/hooks";
import convert from "color-convert";
import Autoplay from "embla-carousel-autoplay";
import { useRouter } from "next/router";
import { useEffect, useState, useRef } from "react";
import { useMediaQuery } from "usehooks-ts";
import { getMasterdata } from "../../../services/services";
import useTrans from "@/hooks/useTrans";
import { isNil } from "lodash";

export function Search4({
  options: { searchFields, handleSearch, delay, threshold, style },
  url,
  primaryColor,
}) {
  const trans = useTrans();
  const isThreshold = useMediaQuery(`(max-width:${threshold || 1000}px)`);

  const [dynamicFilters, setDynamicFilters] = useState([]);
  const [masterdataArray, setMasterdataArray] = useState([]);
  const [sortOption, setSortOption] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [embla, setEmbla] = useState(null);

  const [transform, setTransform] = useState(dynamicFilters.map(() => false));
  const ref = useClickOutside(() =>
    setTransform(dynamicFilters.map(() => false)),
  );

  const convertColor = convert.hex.hsl(primaryColor);
  const hoverColor = `hsla(${convertColor[0]}, ${convertColor[1]}%, ${convertColor[2]}%, 0.1)`;
  const router = useRouter();
  const query = router.query;

  const [autoplay, setAutoPlay] = useState(null);
  const debounceTimersRef = useRef({});
  const TEXT_DEBOUNCE_MS = 300;
  const containerRef = useRef(null);
  const userInteractedRef = useRef(false);

  const componentStyle = style;

  const wrapperStyle = {
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
    gap: "1rem",
  };

  const searchFieldStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    width: isThreshold ? "100%" : 320,
    labelStyle: {
      fontWeight: 600,
    },
  };

  useEffect(() => {
    if (!Array.isArray(searchFields)) {
      return;
    }
    const promiseArray = [];

    searchFields.forEach(({ masterdata }, index) => {
      if (masterdata) {
        const promise = getMasterdata(url, masterdata.key).then((res) => {
          let selectData = res;
          if (masterdata.metadataType) {
            selectData = res.filter((e) => {
              const metadataType = e.metadata.find(
                (metadataSingle) =>
                  metadataSingle.type === masterdata.metadataType,
              );
              return metadataType !== undefined;
            });
          }

          return {
            data: selectData.map((selectDataSingle) => ({
              label: selectDataSingle.label,
              value: selectDataSingle.value,
              actualValue: selectDataSingle.value,
            })),
            index,
            key: masterdata.key,
          };
        });
        promiseArray.push(promise);
      }
    });

    Promise.all(promiseArray).then((resArray) => {
      setMasterdataArray(resArray);

      const dynamicFiltersWithMasterdata = searchFields.map(
        ({
          type,
          key,
          searchType,
          masterdata,
          label,
          placeholder,
          prefix = "",
        }) => {
          let value = "";
          if (type === "multiple") {
            value = [];
            const relatedMasterdata = masterdata
              ? resArray.find((r) => r.key === masterdata.key)
              : null;

            if (relatedMasterdata) {
              if (router.query[masterdata.key]) {
                const msdt = Array.isArray(router.query[masterdata.key])
                  ? router.query[masterdata.key]
                  : [router.query[masterdata.key]];
                msdt.forEach((e) => {
                  const found = relatedMasterdata.data.find(
                    (item) => item.actualValue === e,
                  );
                  if (found && !value.includes(found.value)) {
                    value.push(prefix + found.value);
                  }
                });
              }
            }
            // }
          } else if (type === "single") {
            value = "";

            const relatedMasterdata = masterdata
              ? resArray.find((r) => r.key === masterdata.key)
              : null;
            if (relatedMasterdata) {
              const msdt = Array.isArray(router.query[masterdata.key])
                ? router.query[masterdata.key][0]
                : router.query[masterdata.key];
              value =
                relatedMasterdata.data.find((item) => item.actualValue === msdt)
                  ?.value || "";
              if (value !== "") {
                value = prefix + value;
              }
            }
          } else if (type === "text") {
            if (router.query[key] == undefined) value = "";
            else {
              value = Array.isArray(router.query[key])
                ? router.query[key][0]
                : router.query[key];
            }
          }

          return {
            type,
            key,
            searchType,
            masterdata,
            label,
            placeholder,
            value,
            prefix,
          };
        },
      );
      setDynamicFilters(dynamicFiltersWithMasterdata);
    });
  }, []);

  useEffect(() => {
    if (query.type && query.value && masterdataArray.length > 0) {
      const dynamicFilterTemp = [...dynamicFilters];
      if (query.type === "masterdata" && query.key) {
        const masterdataSingle = masterdataArray.find(({ key }) => {
          return key === query.key;
        });
        if (masterdataSingle) {
          const dataSingle = masterdataSingle.data.find((dataSingle) => {
            return dataSingle.actualValue === query.value;
          });

          if (dataSingle) {
            dynamicFilterTemp[masterdataSingle.index].value =
              searchFields[masterdataSingle.index].type === "multiple"
                ? [dataSingle.value]
                : dataSingle.value;
          }
        }
      }

      if (query.type === "text" && query.index) {
        dynamicFilterTemp[query.index].value = query.value;
      }

      setDynamicFilters(dynamicFilterTemp);
    }
  }, [query, masterdataArray]);

  useEffect(() => {
    if (handleSearch) {
      handleSearch(dynamicFilters, sortOption);

      // Auto scroll down after initiating search when user interacts
      if (userInteractedRef.current) {
        setTimeout(() => {
          if (containerRef.current && typeof window !== "undefined") {
            const rect = containerRef.current.getBoundingClientRect();
            const targetTop = rect.bottom + window.scrollY + 16;
            window.scrollTo({ top: targetTop, behavior: "smooth" });
          }
        }, 300);
      }
    }
  }, [dynamicFilters, sortOption]);

  useEffect(() => {
    if (!embla) return;

    const handleLoad = () => {
      embla.reInit();
    };
    document
      .querySelectorAll(".mantine-Carousel-slide img")
      .forEach((img) => img.addEventListener("load", handleLoad));

    return () => {
      document
        .querySelectorAll(".mantine-Carousel-slide img")
        .forEach((img) => img.removeEventListener("load", handleLoad));
    };
  }, [embla]);

  useEffect(() => {
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (initialized) {
      setAutoPlay(Autoplay({ delay: delay > 0 ? delay * 1000 : 5000 }));
    }
  }, [initialized]);

  useEffect(() => {
    return () => {
      const timers = debounceTimersRef.current || {};
      Object.values(timers).forEach((t) => t && clearTimeout(t));
    };
  }, []);

  useEffect(() => {
    const queryObj = {};

    for (let filterItem of dynamicFilters) {
      if (Array.isArray(filterItem.value)) {
        if (filterItem.value.length != 0) {
          queryObj[filterItem.masterdata?.key] = filterItem.value.map((str) => str.split(":")[2]);
        }
      } else if (filterItem.key != "name" && !isNil(filterItem.value) && filterItem.value != "") {
        queryObj[filterItem.masterdata?.key] = filterItem.value.split(":")[2];
      }
      if (filterItem.key == "name" && filterItem.value != "") {
        queryObj["name"] = filterItem.value;
      }
    }

    console.log("query", queryObj)
    router.push(
      {
        pathname: `/${router.query.id}`,
        query: queryObj
      },
      undefined,
      { shallow: true }
    )
  }, [dynamicFilters])

  if (!Array.isArray(searchFields)) {
    return <></>;
  }

  const searchFieldJSX = dynamicFilters.map(
    ({ type, label, placeholder, prefix = "" }, index) => {
      switch (type) {
        case "text":
          return (
            <div
              key={index}
              style={{
                ...searchFieldStyle,
                ...(componentStyle?.searchFieldStyle || {}),
              }}
            >
              <Text
                style={{
                  ...searchFieldStyle.labelStyle,
                  ...(componentStyle?.searchFieldStyle?.labelStyle || {}),
                }}
              >
                {label}
              </Text>
              <TextInput
                styles={() => ({
                  input: {
                    "&:focus-within": {
                      border: `1px solid ${primaryColor};`,
                    },
                  },
                })}
                defaultValue={dynamicFilters[index].value}
                placeholder={placeholder}
                onChange={(e) => {
                  const value = e.target.value;
                  const timers = debounceTimersRef.current;
                  if (timers[index]) {
                    clearTimeout(timers[index]);
                  }
                  timers[index] = setTimeout(() => {
                    const dynamicFilterTemp = [...dynamicFilters];
                    dynamicFilterTemp[index].value = value;
                    setDynamicFilters(dynamicFilterTemp);
                    userInteractedRef.current = true;
                  }, TEXT_DEBOUNCE_MS);
                }}
              />
            </div>
          );

        case "single": {
          const selectData =
            masterdataArray
              .find((e) => e.index === index)
              ?.data?.map((e) => ({
                ...e,
                value: prefix + e.value,
              })) || [];

          return (
            <div
              key={index}
              style={{
                ...searchFieldStyle,
                ...(componentStyle?.searchFieldStyle || {}),
              }}
            >
              <Text
                style={{
                  ...searchFieldStyle.labelStyle,
                  ...(componentStyle?.searchFieldStyle?.labelStyle || {}),
                }}
              >
                {label}
              </Text>
              <Select
                placeholder={placeholder}
                dropdownPosition="bottom"
                value={dynamicFilters[index].value}
                searchable
                data={[{ label: trans.all, value: "" }, ...selectData]}
                transitionDuration={80}
                transitionTimingFunction="ease"
                onClick={() => {
                  const transformTemp = [...transform];
                  transformTemp[index] = !transformTemp[index];
                  setTransform(transformTemp);
                }}
                onChange={(value) => {
                  const dynamicFilterTemp = [...dynamicFilters];
                  dynamicFilterTemp[index].value = value;
                  setDynamicFilters(dynamicFilterTemp);
                  userInteractedRef.current = true;
                }}
                styles={() => ({
                  itemsWrapper: {
                    // gap: 8,
                    // borderRadius: 0,
                    // padding: 0,
                  },
                  input: {
                    "&:focus-within": {
                      border: `1px solid ${primaryColor};`,
                    },
                  },
                  item: {
                    // padding: "4px 16px 4px 16px",
                    // fontSize: 16,
                    // textAlign: "left",
                    "&[data-hovered]": {
                      background: hoverColor,
                    },

                    "&[data-selected]": {
                      background: primaryColor,
                    },
                  },
                })}
              ></Select>
            </div>
          );
        }

        case "multiple": {
          const selectData =
            masterdataArray
              .find((e) => e.index === index)
              ?.data?.map((e) => ({
                ...e,
                value: prefix + e.value,
              })) || [];
          const value = dynamicFilters[index].value;

          // const valueComponent = ({label, value}, {...others}) => {
          //     return <div></div>
          // }

          return (
            <div
              key={index}
              style={{
                ...searchFieldStyle,
                ...(componentStyle?.searchFieldStyle || {}),
              }}
            >
              <Text
                style={{
                  ...searchFieldStyle.labelStyle,
                  ...(componentStyle?.searchFieldStyle?.labelStyle || {}),
                }}
              >
                {label}
              </Text>
              <MultiSelect
                ref={ref}
                dropdownPosition="bottom"
                value={value}
                data={selectData}
                clearable
                searchable
                onClick={() => {
                  const transformTemp = [...transform];
                  transformTemp[index] = !transformTemp[index];
                  setTransform(transformTemp);
                }}
                onChange={(value) => {
                  const dynamicFilterTemp = [...dynamicFilters];
                  dynamicFilterTemp[index].value = value;
                  setDynamicFilters(dynamicFilterTemp);
                  userInteractedRef.current = true;
                }}
                styles={() => ({
                  rightSection: {
                    pointerEvents: "none",
                  },
                  input: {
                    // border: "none",
                    // borderBottom: "1px solid hsla(249, 8%, 92%, 1)",
                    "&:focus-within": {
                      border: `1px solid ${primaryColor};`,
                    },
                  },
                  itemsWrapper: {
                    // gap: 8,
                    // borderRadius: 16,
                    // padding: "16px 0 16px 0",
                  },
                  item: {
                    // padding: "4px 16px 4px 16px",
                    // fontSize: 16,
                    // textAlign: "left",
                    "&[data-hovered]": {
                      background: hoverColor,
                    },
                  },
                  values: {
                    // display: "flex",
                    // flexDirection: "row",
                    // gap: 2,
                    // flexWrap: "nowrap",
                    // overflowX: "auto",
                  },
                  value: {
                    // borderRadius: 1000,
                    // padding: 10,
                    // fontSize: 12,
                    // display: "flex",
                    // alignItems: "center",
                    // gap: 4,
                    // background: primaryColor,
                    // color: "white",
                    // svg: {
                    //   color: "white",
                    // },
                  },
                })}
              />
            </div>
          );
        }
        default:
          return <div key={index}></div>;
      }
    },
  );

  return (
    <div
      style={{
        ...wrapperStyle,
        ...(componentStyle || {}),
      }}
    >
      {searchFieldJSX}
    </div>
  );
}
